import { useCallback, useEffect, useState } from 'react'
import { functions } from '../../config/firebase'
import getDoc from '../../utils/db/getDoc'
import {
  getCallableErrorMessage,
  normalizeSyncDate,
  normalizeSyncDatesDoc,
} from '../ProductsSyncMenu/syncDateUtils'

const SYNC_DATE_KEY = 'premiumPromoSchedules'
const APPLY_PRICES_CALLABLE_NAME = 'applyPremiumPromoPrices'
const CALLABLE_TIMEOUT = 300000

const loadSyncDate = () =>
  getDoc({ path: 'api', docId: 'requestsDates' }).then((doc) => {
    const dates = normalizeSyncDatesDoc(doc)
    return dates[SYNC_DATE_KEY] ?? null
  })

const buildSyncSummary = (data) => {
  if (!data || typeof data !== 'object') {
    return null
  }

  const fromApi = data.schedulesFromApi
  const upserted = data.schedulesUpserted
  const archived = data.schedulesArchived
  const matched = data.productsMatched
  const total = data.productsTotal

  if (
    typeof fromApi !== 'number' &&
    typeof upserted !== 'number' &&
    typeof archived !== 'number' &&
    typeof matched !== 'number'
  ) {
    return null
  }

  const parts = []

  if (typeof fromApi === 'number') {
    parts.push(`з API: ${fromApi}`)
  }

  if (typeof upserted === 'number') {
    parts.push(`оновлено: ${upserted}`)
  }

  if (typeof archived === 'number' && archived > 0) {
    parts.push(`архів: ${archived}`)
  }

  if (typeof matched === 'number' && typeof total === 'number') {
    parts.push(`товари: ${matched}/${total}`)
  }

  return parts.join(' · ')
}

const buildSyncWarning = (data) => {
  if (!data || typeof data !== 'object') {
    return null
  }

  if (data.schedulesFromApi === 0) {
    if (typeof data.schedulesArchived === 'number' && data.schedulesArchived > 0) {
      return null
    }

    return 'Cashbox не повернув жодної акції — нових даних з API немає'
  }

  if (typeof data.schedulesUpserted === 'number' && data.schedulesUpserted === 0) {
    return 'Жодну акцію не вдалося записати в базу'
  }

  return null
}

const buildPriceApplySummary = (data) => {
  if (!data || typeof data !== 'object') {
    return null
  }

  const applied = data.applied
  const cleared = data.cleared
  const failed = data.failed

  if (
    typeof applied !== 'number' &&
    typeof cleared !== 'number' &&
    typeof failed !== 'number'
  ) {
    return null
  }

  const parts = []

  if (typeof applied === 'number') {
    parts.push(`ціни: ${applied}`)
  }

  if (typeof cleared === 'number' && cleared > 0) {
    parts.push(`очищено: ${cleared}`)
  }

  if (typeof failed === 'number' && failed > 0) {
    parts.push(`помилки: ${failed}`)
  }

  return parts.join(' · ')
}

const joinSummary = (...summaries) => summaries.filter(Boolean).join(' · ')

export const usePromoSchedulesSync = () => {
  const [lastSyncDate, setLastSyncDate] = useState(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [result, setResult] = useState(null)

  const reloadSyncDate = useCallback(async () => {
    const date = await loadSyncDate()
    setLastSyncDate(date)
    return date
  }, [])

  useEffect(() => {
    reloadSyncDate()
  }, [reloadSyncDate])

  const runSync = useCallback(async () => {
    setIsSyncing(true)
    setResult(null)

    try {
      const callable = functions.httpsCallable('syncPremiumPromoSchedules', {
        timeout: CALLABLE_TIMEOUT,
      })
      const resp = await callable()
      const syncData = resp.data || {}
      let applyPricesData = {}
      let priceApplyError = null

      try {
        const applyPricesCallable = functions.httpsCallable(APPLY_PRICES_CALLABLE_NAME, {
          timeout: CALLABLE_TIMEOUT,
        })
        const applyPricesResp = await applyPricesCallable()
        applyPricesData = applyPricesResp.data || {}
      } catch (applyError) {
        console.error('applyPremiumPromoPrices failed:', applyError)
        priceApplyError = getCallableErrorMessage(applyError)
      }

      const parsedDate = normalizeSyncDate(syncData.lastSyncDate)
      const warning = buildSyncWarning(syncData)

      if (parsedDate && (syncData.schedulesFromApi > 0 || syncData.schedulesArchived > 0)) {
        setLastSyncDate(parsedDate)
      }

      await reloadSyncDate()

      const nextResult = {
        ok: !warning && !priceApplyError,
        summary: joinSummary(buildSyncSummary(syncData), buildPriceApplySummary(applyPricesData)),
        warning: warning || priceApplyError,
      }
      setResult(nextResult)
      return nextResult
    } catch (error) {
      console.error('syncPremiumPromoSchedules failed:', error)
      const nextResult = {
        ok: false,
        message: getCallableErrorMessage(error),
      }
      setResult(nextResult)
      return nextResult
    } finally {
      setIsSyncing(false)
    }
  }, [reloadSyncDate])

  return {
    lastSyncDate,
    isSyncing,
    result,
    runSync,
  }
}

export default usePromoSchedulesSync
